import { AsyncPipe, NgTemplateOutlet } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatIconButton } from '@angular/material/button'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import { MatToolbar } from '@angular/material/toolbar'
import { ActivatedRoute } from '@angular/router'
import {
  ElderBasicPaneLayoutComponent,
  ElderHeaderComponent,
  ElderPaneActionsComponent,
  ElderPaneComponent,
  ElderPaneContentComponent,
  ElderPaneHeaderComponent,
  ElderPaneSubtitleComponent,
  ElderPaneTitleComponent,
  ElderThemeDirective,
  ElderToastService,
  UuidUtil,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable } from 'rxjs'
import { IdValidator } from '../../../../features/validators/id-validator'
import { ProductionCode } from '../production-code'
import { ProductionCodeService } from '../production-code.service'

@Component({
  selector: 'app-production-code-detail',
  templateUrl: './production-code-detail.component.html',
  styleUrls: ['./production-code-detail.component.scss'],
  imports: [
    MatToolbar,
    ElderThemeDirective,
    MatIconButton,
    MatIcon,
    NgTemplateOutlet,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    FormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    ElderHeaderComponent,
    AsyncPipe,
    ElderTranslatePipe,
    ElderPaneComponent,
    ElderPaneHeaderComponent,
    ElderPaneActionsComponent,
    ElderPaneContentComponent,
    ElderPaneTitleComponent,
    ElderBasicPaneLayoutComponent,
    ElderPaneSubtitleComponent,
  ],
})
export class ProductionCodeDetailComponent implements OnInit {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  private readonly _prodCode$: BehaviorSubject<ProductionCode> = new BehaviorSubject(null)

  private _creationMode: boolean

  @Input()
  public embedded = false

  @Output()
  public close: EventEmitter<Event> = new EventEmitter<Event>()

  public readonly idValidatorPattern = IdValidator.pattern
  private productionCodeService = inject(ProductionCodeService)
  private toastService = inject(ElderToastService)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const route = inject(ActivatedRoute)
    this.prodCode = route.snapshot.data['prodCode']
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {
    if (!this.prodCode) {
      this.prodCode = this.initNew()
    }
  }

  /***************************************************************************
   *                                                                         *
   * Getter / Setters                                                        *
   *                                                                         *
   **************************************************************************/

  public get prodCode$(): Observable<ProductionCode> {
    return this._prodCode$.asObservable()
  }

  public get prodCode(): ProductionCode {
    return this._prodCode$.getValue()
  }

  @Input()
  public set prodCode(prodCode: ProductionCode) {
    if (prodCode) {
      this._creationMode = !prodCode.id

      this._prodCode$.next(prodCode)
    }
  }

  public get creationMode(): boolean {
    return this._creationMode
  }

  public get deletionEnabled(): boolean {
    return !this.creationMode
  }

  /***************************************************************************
   *                                                                         *
   * Component Events                                                        *
   *                                                                         *
   **************************************************************************/

  public onSave(event: Event): void {
    this.save()
  }

  public onDelete(event: Event): void {
    this.delete()
  }

  public onClose(event: Event): void {
    this.emitClose(event)
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private save(): void {
    this.productionCodeService.update(this.prodCode).subscribe({
      next: (updated) => {
        this.prodCode = updated
        this.toastService.info('messages.persisting_successful')
      },
      error: (err) => this.toastService.error('messages.saving_failed', err),
    })
  }

  private delete(): void {
    this.productionCodeService.delete(this.prodCode).subscribe({
      next: (_) => {
        this.toastService.info('messages.deleting_successful')
        this.emitClose()
      },
      error: (err) => this.toastService.error('messages.deleting_failed', err),
    })
  }

  private emitClose(event?: Event): void {
    this.close.emit(event)
  }

  private initNew(): ProductionCode {
    return <ProductionCode>{
      id: UuidUtil.strongUuid4(),
    }
  }
}
